import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Calculator, Save } from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/DatePicker";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { formatCurrency, formatDate } from "@/lib/format";

const EMPTY_HOLDING = { investor_id: "", amount: "", instrument: "SAFE", round_name: "", ownership_pct: "", invested_on: "" };
const EMPTY_SCENARIO = { name: "", pre_money: "", raise_amount: "", option_pool_pct: "" };

export default function Rounds() {
  const { company } = useAuth();
  const [holdings, setHoldings] = useState(null);
  const [investors, setInvestors] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [valuation, setValuation] = useState("");
  const [hDialog, setHDialog] = useState(false);
  const [hForm, setHForm] = useState(EMPTY_HOLDING);
  const [saving, setSaving] = useState(false);
  const [sForm, setSForm] = useState(EMPTY_SCENARIO);
  const [safes, setSafes] = useState([]);
  const [preview, setPreview] = useState(null);
  const [computing, setComputing] = useState(false);

  const load = async () => {
    if (!company) return;
    try {
      const [h, inv, sc] = await Promise.all([
        api.get(`/companies/${company.id}/holdings`),
        api.get(`/companies/${company.id}/investors`),
        api.get(`/companies/${company.id}/round-scenarios`).catch(() => ({ data: [] })),
      ]);
      setHoldings(h.data);
      setValuation(h.data.latest_valuation ?? "");
      setInvestors(inv.data);
      setScenarios(sc.data);
    } catch (e) { console.error("Failed to load rounds data", e); }
    finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [company]);

  const saveValuation = async () => {
    try {
      await api.put(`/companies/${company.id}/valuation`, { latest_valuation: valuation === "" ? null : Number(valuation) });
      toast.success("Valuation saved");
      load();
    } catch { toast.error("Could not save valuation"); }
  };

  const addHolding = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/companies/${company.id}/holdings`, {
        ...hForm, amount: Number(hForm.amount),
        ownership_pct: hForm.ownership_pct === "" ? null : Number(hForm.ownership_pct),
      });
      toast.success("Holding recorded");
      setHDialog(false); setHForm(EMPTY_HOLDING);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save holding"); }
    finally { setSaving(false); }
  };

  const removeHolding = async (id) => {
    try { await api.delete(`/holdings/${id}`); load(); toast.success("Holding removed"); }
    catch { toast.error("Delete failed"); }
  };

  const scenarioPayload = () => ({
    name: sForm.name || "Untitled scenario",
    pre_money: Number(sForm.pre_money), raise_amount: Number(sForm.raise_amount || 0),
    option_pool_pct: Number(sForm.option_pool_pct || 0),
    safes: safes.map((s) => ({ name: s.name || "SAFE", amount: Number(s.amount || 0), cap: Number(s.cap || 0) })),
  });

  const runPreview = async () => {
    if (!sForm.pre_money) { toast.error("Enter a pre-money valuation"); return; }
    setComputing(true);
    try {
      const { data } = await api.post(`/companies/${company.id}/round-scenarios/preview`, scenarioPayload());
      setPreview(data);
    } catch (err) { if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Could not compute"); }
    finally { setComputing(false); }
  };

  const saveScenario = async () => {
    if (!sForm.pre_money) { toast.error("Enter a pre-money valuation"); return; }
    try {
      await api.post(`/companies/${company.id}/round-scenarios`, scenarioPayload());
      toast.success("Scenario saved");
      load();
    } catch (err) { if (err?.response?.status !== 402) toast.error(err?.response?.data?.detail || "Could not save"); }
  };

  const removeScenario = async (id) => {
    try { await api.delete(`/round-scenarios/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  const ResultPanel = ({ r }) => (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" data-testid="scenario-result">
      <div className="rounded-lg bg-secondary/60 p-3"><div className="text-[11px] text-muted-foreground">Post-money</div><div className="h-display text-lg font-semibold tabular-nums">{formatCurrency(r.post_money)}</div></div>
      <div className="rounded-lg bg-secondary/60 p-3"><div className="text-[11px] text-muted-foreground">New investors</div><div className="h-display text-lg font-semibold tabular-nums">{r.new_investor_pct}%</div></div>
      <div className="rounded-lg bg-secondary/60 p-3"><div className="text-[11px] text-muted-foreground">SAFEs convert</div><div className="h-display text-lg font-semibold tabular-nums">{r.safe_total_pct}%</div></div>
      <div className="rounded-lg bg-secondary/60 p-3"><div className="text-[11px] text-muted-foreground">Option pool</div><div className="h-display text-lg font-semibold tabular-nums">{r.option_pool_pct}%</div></div>
      <div className="rounded-lg bg-secondary/60 p-3"><div className="text-[11px] text-muted-foreground">Existing holders keep</div><div className="h-display text-lg font-semibold tabular-nums text-primary">{r.existing_holders_pct}%</div></div>
      <div className="rounded-lg bg-secondary/60 p-3"><div className="text-[11px] text-muted-foreground">Total dilution</div><div className="h-display text-lg font-semibold tabular-nums text-amber-600">{r.total_dilution_pct}%</div></div>
    </div>
  );

  return (
    <AppShell title="Rounds & Ownership" subtitle="Investor holdings, valuation and dilution scenarios">
      {loading ? <p className="text-sm text-muted-foreground">Loading…</p> : (
        <Tabs defaultValue="holdings">
          <TabsList data-testid="rounds-tabs">
            <TabsTrigger value="holdings" data-testid="tab-holdings">Holdings</TabsTrigger>
            <TabsTrigger value="scenarios" data-testid="tab-scenarios">Round scenarios</TabsTrigger>
          </TabsList>

          <TabsContent value="holdings" className="mt-4 space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <Card className="rounded-xl border bg-card p-4">
                <div className="text-[11px] text-muted-foreground">Total invested</div>
                <div className="h-display text-2xl font-semibold tabular-nums" data-testid="holdings-total">{formatCurrency(holdings?.total_invested || 0)}</div>
              </Card>
              <Card className="rounded-xl border bg-card p-4 sm:col-span-2">
                <div className="text-[11px] text-muted-foreground">Latest company valuation (drives investor MOIC)</div>
                <div className="mt-1 flex gap-2">
                  <Input type="number" value={valuation} onChange={(e) => setValuation(e.target.value)} placeholder="e.g. 12000000" className="h-9" data-testid="valuation-input" />
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={saveValuation} data-testid="valuation-save"><Save className="h-3.5 w-3.5" /> Save</Button>
                </div>
              </Card>
            </div>

            <div className="flex items-center justify-between">
              <h2 className="h-display text-base font-semibold text-foreground">Investment records</h2>
              <Button size="sm" className="gap-1.5" onClick={() => setHDialog(true)} data-testid="holding-add-btn"><Plus className="h-4 w-4" /> Record investment</Button>
            </div>
            {(holdings?.holdings || []).length === 0 ? (
              <Card className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">No holdings recorded yet. Record each investor's check to power their portfolio view.</Card>
            ) : (
              <div className="space-y-2">
                {holdings.holdings.map((h) => (
                  <Card key={h.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card p-4" data-testid={`holding-row-${h.id}`}>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-foreground">{h.investor_name}</div>
                      <div className="text-xs text-muted-foreground">{h.instrument}{h.round_name ? ` · ${h.round_name}` : ""}{h.invested_on ? ` · ${formatDate(h.invested_on)}` : ""}</div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="h-display text-sm font-semibold tabular-nums">{formatCurrency(h.amount)}</div>
                        {h.ownership_pct != null && <div className="text-xs tabular-nums text-muted-foreground">{h.ownership_pct}% ownership</div>}
                      </div>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => removeHolding(h.id)} data-testid={`holding-delete-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="scenarios" className="mt-4">
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-[400px_1fr]">
              <Card className="h-fit rounded-xl border bg-card p-5">
                <div className="mb-3 flex items-center gap-2"><Calculator className="h-4 w-4 text-primary" /><h2 className="text-sm font-semibold text-foreground">Model a round</h2></div>
                <div className="space-y-3">
                  <div><Label>Scenario name</Label><Input value={sForm.name} onChange={(e) => setSForm((f) => ({ ...f, name: e.target.value }))} placeholder="Seed round A" className="mt-1" data-testid="scenario-name" /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label>Pre-money ($)</Label><Input type="number" value={sForm.pre_money} onChange={(e) => setSForm((f) => ({ ...f, pre_money: e.target.value }))} className="mt-1" data-testid="scenario-premoney" /></div>
                    <div><Label>Raise ($)</Label><Input type="number" value={sForm.raise_amount} onChange={(e) => setSForm((f) => ({ ...f, raise_amount: e.target.value }))} className="mt-1" data-testid="scenario-raise" /></div>
                  </div>
                  <div><Label>New option pool (% of post)</Label><Input type="number" value={sForm.option_pool_pct} onChange={(e) => setSForm((f) => ({ ...f, option_pool_pct: e.target.value }))} className="mt-1" data-testid="scenario-pool" /></div>
                  <div>
                    <div className="flex items-center justify-between"><Label>Outstanding SAFEs (post-money cap)</Label>
                      <Button type="button" size="sm" variant="ghost" className="h-7 gap-1 text-xs" onClick={() => setSafes((s) => [...s, { name: `SAFE ${s.length + 1}`, amount: "", cap: "" }])} data-testid="scenario-add-safe"><Plus className="h-3 w-3" /> Add</Button>
                    </div>
                    {safes.map((s, i) => (
                      <div key={i} className="mt-2 flex items-center gap-2">
                        <Input value={s.name} onChange={(e) => setSafes((arr) => arr.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Name" className="h-8 text-xs" />
                        <Input type="number" value={s.amount} onChange={(e) => setSafes((arr) => arr.map((x, j) => j === i ? { ...x, amount: e.target.value } : x))} placeholder="Amount" className="h-8 text-xs" data-testid={`safe-amount-${i}`} />
                        <Input type="number" value={s.cap} onChange={(e) => setSafes((arr) => arr.map((x, j) => j === i ? { ...x, cap: e.target.value } : x))} placeholder="Cap" className="h-8 text-xs" data-testid={`safe-cap-${i}`} />
                        <button onClick={() => setSafes((arr) => arr.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <Button className="flex-1 gap-1.5" onClick={runPreview} disabled={computing} data-testid="scenario-preview-btn">
                      {computing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calculator className="h-4 w-4" />} Compute
                    </Button>
                    <Button variant="outline" className="gap-1.5" onClick={saveScenario} data-testid="scenario-save-btn"><Save className="h-4 w-4" /> Save</Button>
                  </div>
                </div>
              </Card>

              <div className="space-y-4">
                {preview && <Card className="rounded-xl border bg-card p-5"><h3 className="mb-3 text-sm font-semibold text-foreground">Result</h3><ResultPanel r={preview} /></Card>}
                <div>
                  <h3 className="mb-2 text-sm font-semibold text-foreground">Saved scenarios</h3>
                  {scenarios.length === 0 ? (
                    <Card className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">No saved scenarios yet.</Card>
                  ) : (
                    <div className="space-y-2">
                      {scenarios.map((s) => (
                        <Card key={s.id} className="rounded-xl border bg-card p-4" data-testid={`scenario-row-${s.id}`}>
                          <div className="mb-2 flex items-center justify-between">
                            <div>
                              <div className="text-sm font-semibold text-foreground">{s.name}</div>
                              <div className="text-xs text-muted-foreground">{formatCurrency(s.pre_money)} pre · raising {formatCurrency(s.raise_amount)}</div>
                            </div>
                            <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-red-600" onClick={() => removeScenario(s.id)} data-testid={`scenario-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                          </div>
                          <ResultPanel r={s.result} />
                        </Card>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      )}

      <Dialog open={hDialog} onOpenChange={setHDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Record an investment</DialogTitle></DialogHeader>
          <form onSubmit={addHolding} className="space-y-3">
            <div>
              <Label>Investor</Label>
              <Select value={hForm.investor_id} onValueChange={(v) => setHForm((f) => ({ ...f, investor_id: v }))}>
                <SelectTrigger className="mt-1" data-testid="holding-form-investor"><SelectValue placeholder="Select investor" /></SelectTrigger>
                <SelectContent>{investors.map((i) => <SelectItem key={i.id} value={i.id}>{i.name} ({i.email})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Amount ($)</Label><Input type="number" value={hForm.amount} onChange={(e) => setHForm((f) => ({ ...f, amount: e.target.value }))} required className="mt-1" data-testid="holding-form-amount" /></div>
              <div>
                <Label>Instrument</Label>
                <Select value={hForm.instrument} onValueChange={(v) => setHForm((f) => ({ ...f, instrument: v }))}>
                  <SelectTrigger className="mt-1" data-testid="holding-form-instrument"><SelectValue /></SelectTrigger>
                  <SelectContent>{(holdings?.instruments || ["SAFE", "Priced round", "Convertible note", "Other"]).map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Round</Label><Input value={hForm.round_name} onChange={(e) => setHForm((f) => ({ ...f, round_name: e.target.value }))} placeholder="Seed" className="mt-1" data-testid="holding-form-round" /></div>
              <div><Label>Ownership %</Label><Input type="number" step="0.001" value={hForm.ownership_pct} onChange={(e) => setHForm((f) => ({ ...f, ownership_pct: e.target.value }))} className="mt-1" data-testid="holding-form-pct" /></div>
            </div>
            <div><Label>Invested on</Label><DatePicker value={hForm.invested_on} onChange={(v) => setHForm((f) => ({ ...f, invested_on: v }))} className="mt-1" data-testid="holding-form-date" /></div>
            <Button type="submit" disabled={saving || !hForm.investor_id} className="w-full gap-2" data-testid="holding-form-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Record investment
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
